import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { TrendingUp, Target, Users, Zap, Globe, Award, ArrowUpRight, BarChart3, Rocket, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Investors = () => {
  const navigate = useNavigate();

  const metrics = [
    { value: "10K+", label: "Active Users", growth: "+150%", icon: Users, color: "from-blue-500 to-blue-600" },
    { value: "10min", label: "Avg Response", growth: "Industry Leading", icon: Zap, color: "from-amber-500 to-orange-600" },
    { value: "5+", label: "Micro Markets Live", growth: "+200%", icon: Globe, color: "from-emerald-500 to-teal-600" },
    { value: "80%", label: "User Retention", growth: "+15%", icon: Award, color: "from-purple-500 to-pink-600" }
  ];

  const opportunities = [
    {
      title: "Massive Market Opportunity",
      description: "India's home services market is valued at $50B+ with a CAGR of 20%. We're capturing a rapidly growing segment with first-mover advantage in instant service delivery.",
      icon: TrendingUp,
      stats: ["$50B+ TAM", "20% CAGR", "Under-penetrated"]
    },
    {
      title: "Proven Business Model",
      description: "Asset-light marketplace with strong unit economics. Recurring revenue streams, positive gross margins, and clear path to profitability at scale.",
      icon: Target,
      stats: ["Asset-Light", "40%+ Gross Margin", "Recurring Revenue"]
    },
    {
      title: "Technology Moat",
      description: "Proprietary AI-powered matching engine, real-time logistics optimization, and seamless payment infrastructure create significant competitive advantages.",
      icon: Rocket,
      stats: ["AI-Powered", "Real-time Ops", "Scalable Tech"]
    }
  ];

  const traction = [
    { metric: "10X", description: "User Growth YoY" },
    { metric: "3X", description: "Revenue Growth" },
    { metric: "4.8/5", description: "User Rating" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-white dark:from-[#0a0a0a] dark:via-[#0f0f0f] dark:to-[#0a0a0a]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-[#0a0a0a]/95 backdrop-blur-md shadow-md border-b border-gray-100 dark:border-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <img 
                src="https://customer-assets.emergentagent.com/job_ec527428-3aec-42a4-a1f7-d2e66c0be0df/artifacts/hktez0bk_logo_transs.png" 
                alt="doorush logo" 
                className="h-10 w-auto"
              />
            </div>
            <Button onClick={() => navigate('/')} variant="outline" className="border-2 border-[#007e7a] text-[#007e7a] hover:bg-[#007e7a] hover:text-white transition-all duration-300">
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-40 pb-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#007e7a]/5 via-transparent to-[#007e7a]/5"></div>
        <div className="absolute top-20 right-20 w-[500px] h-[500px] bg-gradient-to-br from-[#007e7a]/20 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-[500px] h-[500px] bg-gradient-to-tr from-[#007e7a]/10 to-transparent rounded-full blur-3xl"></div>
        
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <Badge className="bg-[#007e7a]/10 text-[#007e7a] dark:text-[#00a89d] hover:bg-[#007e7a]/20 border-[#007e7a]/20 backdrop-blur-sm mb-8 px-6 py-2 text-base">
            Investment Opportunity
          </Badge>
          <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold text-gray-900 dark:text-white mb-8 tracking-tight leading-none">
            Invest in the Future<br />
            <span className="bg-gradient-to-r from-[#007e7a] to-[#00a89d] bg-clip-text text-transparent">of Home Services</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-3xl mx-auto mb-10">
            Join us in revolutionizing India's $50B+ home services market. doorush is building the most trusted platform for on-demand household services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {/* <Button 
              size="lg"
              className="bg-[#007e7a] hover:bg-[#006666] text-white text-lg px-10 py-7 rounded-full font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group"
              onClick={() => window.location.href = 'mailto:investors@doorush.in'}
            >
              Request Investor Deck
              <ArrowUpRight className="ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" size={20} />
            </Button> */}
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-[#007e7a] text-[#007e7a] hover:bg-[#007e7a] hover:text-white text-lg px-10 py-7 rounded-full font-semibold transition-all duration-300"
              onClick={() => navigate('/contact')}
            >
              Schedule Meeting
            </Button>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 dark:text-white mb-4 tracking-tight">Key Metrics</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">Demonstrating strong growth and market fit</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {metrics.map((metric, index) => {
              const IconComponent = metric.icon;
              return (
                <Card key={index} className="relative overflow-hidden bg-white dark:bg-[#1a1a1a] border-2 border-gray-100 dark:border-gray-800 hover:border-[#007e7a]/30 dark:hover:border-[#00a89d]/30 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 rounded-2xl group">
                  <div className={`absolute inset-0 bg-gradient-to-br ${metric.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
                  <CardContent className="p-6 relative z-10">
                    <div className={`w-14 h-14 bg-gradient-to-br ${metric.color} rounded-xl flex items-center justify-center mb-4 shadow-lg`}>
                      <IconComponent className="text-white" size={24} />
                    </div>
                    <div className="text-4xl font-bold text-gray-900 dark:text-white mb-2">{metric.value}</div>
                    <div className="text-gray-600 dark:text-gray-300 font-medium mb-2">{metric.label}</div>
                    <div className="text-[#007e7a] dark:text-[#00a89d] text-sm font-semibold flex items-center gap-1">
                      <TrendingUp size={14} />
                      {metric.growth}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Investment Thesis */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 dark:text-white mb-4 tracking-tight">Why Invest in doorush?</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              A unique opportunity to capture India's rapidly expanding home services market
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {opportunities.map((opportunity, index) => {
              const IconComponent = opportunity.icon;
              return (
                <Card key={index} className="relative overflow-hidden bg-white dark:bg-[#1a1a1a] border-2 border-gray-100 dark:border-gray-800 hover:border-[#007e7a]/20 dark:hover:border-[#00a89d]/20 hover:shadow-2xl transition-all duration-500 rounded-3xl group">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#007e7a] to-[#005555] rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-500">
                      <IconComponent className="text-white" size={28} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{opportunity.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">{opportunity.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {opportunity.stats.map((stat, idx) => (
                        <Badge key={idx} className="bg-[#007e7a]/10 text-[#007e7a] dark:text-[#00a89d] border-[#007e7a]/20">
                          {stat}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Traction */}
      {/* <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-br from-[#007e7a] to-[#005555] rounded-3xl p-12 md:p-16 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl"></div>
            </div>
            
            <div className="relative z-10">
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Traction</h2>
                <p className="text-xl text-white/90">Accelerating growth across all key metrics</p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {traction.map((item, index) => (
                  <div key={index} className="text-center">
                    <div className="text-5xl md:text-6xl font-bold text-white mb-2">{item.metric}</div>
                    <div className="text-white/80 text-lg">{item.description}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Contact CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <BarChart3 className="w-16 h-16 text-[#007e7a] dark:text-[#00a89d] mx-auto mb-6" />
          <h2 className="text-5xl font-bold text-gray-900 dark:text-white mb-6 tracking-tight">Let's Build Together</h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-10 leading-relaxed">
            Partner with doorush to transform India's home services landscape. Connect with our investor relations team.
          </p>
          <div className="bg-gray-50 dark:bg-[#1a1a1a] rounded-2xl p-8 border-2 border-gray-100 dark:border-gray-800">
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-3 text-lg">
                <span className="font-semibold text-gray-900 dark:text-white">Email:</span>
                <a href="mailto:investors@doorush.in" className="text-[#007e7a] dark:text-[#00a89d] hover:underline font-medium">
                  investors@doorush.in
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-lg">
                <span className="font-semibold text-gray-900 dark:text-white">Phone:</span>
                <a href="tel:+919092963743" className="text-[#007e7a] dark:text-[#00a89d] hover:underline font-medium">
                  +91 9092963743
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Investors;
